/**
 * @file src/components/VidButtonBank/AccessibilityButton.tsx
 * @exports AccessibilityButton
 * @summary Accessibility settings button for the vID button bank with a SlideIn menu for toggling notification interaction and high contrast theme. Icon colors adapt to high-contrast mode.
 */

import {
	Accessibility,
	CaretDown,
	colors,
	SlideIn,
	ToggleItem,
	useMeasureWatchResize,
	X,
} from "@cerebruminc/cerebellum";
import { useAtom } from "jotai";
import { FC, useCallback, useState } from "react";
import ReactDOM from "react-dom";

import { useWindowDimensions } from "src/hooks";
import { highContrastThemeAtom, requireNotificationInteractionAtom } from "src/shame/shame-atoms";
import { IconBox } from "src/styles/SharedStyles";

import {
	AccessibilityCloseButton,
	AccessibilityMenuHeader,
	AccessibilityMenuPanel,
	AccessibilityMenuRow,
	AccessibilityOverlay,
	ICON_SIZE,
	ICON_SIZE_MOBILE,
	ToggleLabelHelper,
} from "./VidButtonBank.styles";

interface AccessibilityButtonProps {
	ButtonSlot: FC<{
		"aria-label"?: string;
		children: React.ReactNode;
		onClick: () => void;
		type?: React.ButtonHTMLAttributes<HTMLButtonElement>["type"];
		$highContrast?: boolean;
	}>;
}

/** Accessibility settings button that opens a slide-in popover for toggling notification persistence. High-contrast theme toggle is deferred to a future ticket. */
export const AccessibilityButton: FC<AccessibilityButtonProps> = ({ ButtonSlot }) => {
	const [menuOpen, setMenuOpen] = useState(false);
	const [requireInteraction, setRequireInteraction] = useAtom(requireNotificationInteractionAtom);
	const [highContrast, setHighContrast] = useAtom(highContrastThemeAtom);
	const [targetRef, targetMeasure] = useMeasureWatchResize();
	const { width, height } = useWindowDimensions();
	const mobileMode = width < 450;
	const portalTarget = typeof document !== "undefined" ? document.body : null;

	const handleToggleMenu = useCallback(() => {
		setMenuOpen((prev) => !prev);
	}, []);

	const measureOffset = { ...targetMeasure };
	measureOffset.right -= 10; // give some breathing room on the right edge of the screen

	return (
		<>
			<div ref={targetRef}>
				<ButtonSlot
					aria-label="Accessibility settings"
					$highContrast={highContrast}
					onClick={handleToggleMenu}
					type="button"
				>
					<IconBox $size={mobileMode ? ICON_SIZE_MOBILE : ICON_SIZE}>
						{menuOpen ? (
							<CaretDown fill={highContrast ? colors.BLACK : colors.BLUE_100} />
						) : (
							<Accessibility fill={highContrast ? colors.BLACK : colors.BLUE_100} />
						)}
					</IconBox>
				</ButtonSlot>
			</div>

			{portalTarget &&
				ReactDOM.createPortal(
					<AccessibilityOverlay $show={menuOpen} onClick={() => setMenuOpen(false)} />,
					portalTarget,
				)}

			<SlideIn
				clickOut={() => setMenuOpen(false)}
				contentWidth={mobileMode ? 300 : 340}
				height={height}
				openUp
				show={menuOpen}
				targetMeasure={measureOffset}
			>
				<AccessibilityMenuPanel>
					<AccessibilityMenuHeader>Accessibility Options</AccessibilityMenuHeader>

					<AccessibilityCloseButton
						aria-label="Close accessibility options"
						onClick={() => setMenuOpen(false)}
						type="button"
					>
						<IconBox>
							<X fill={colors.COOL_GREY_90} />
						</IconBox>
					</AccessibilityCloseButton>

					<AccessibilityMenuRow>
						<ToggleItem
							active={requireInteraction}
							clickPadding="6px 4px 6px 0"
							label={
								<>
									Extend notification visibility.
									<ToggleLabelHelper>Notifications remain on screen until dismissed manually</ToggleLabelHelper>
								</>
							}
							onToggle={() => setRequireInteraction(!requireInteraction)}
							value="persistent-notifications"
						/>
					</AccessibilityMenuRow>
					<AccessibilityMenuRow>
						<ToggleItem
							active={highContrast}
							clickPadding="6px 4px 6px 0"
							label={
								<>
									High-contrast theme.
									<ToggleLabelHelper>Uses stronger contrast to improve readability and visibility</ToggleLabelHelper>
								</>
							}
							onToggle={() => setHighContrast(!highContrast)}
						/>
					</AccessibilityMenuRow>
				</AccessibilityMenuPanel>
			</SlideIn>
		</>
	);
};
